import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnInit,
} from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs/internal/Observable';
import { of } from 'rxjs/internal/observable/of';
import { catchError, map } from 'rxjs/operators';
import { user } from 'src/app/models/user.model';
import { NotificationService } from 'src/app/services/notification.service';
import { DocentTimetableTableComponent } from 'src/app/docent-timetable/components/docent-timetable-table/docent-timetable-table.component';
import { toDocentTimetableRows } from 'src/app/docent-timetable/docent-timetable-helpers';
import { DocentScheduleHashComponent } from 'src/app/docent-timetable/components/docent-schedule-hash/docent-schedule-hash.component';
import { MeetingLinkDocentActionsComponent } from 'src/app/teams-zoom-config/components/meeting-link-docent-actions/meeting-link-docent-actions.component';
import { DocentTimetableRow } from 'src/app/models/docent-timetable-entry.model';
import { DocentTimetableService } from 'src/app/services/docent-timetable.service';
import { toIsoDate } from 'src/app/utils/date-helper';
import { DEFAULT_RANGE_IN_DAYS } from 'src/app/utils/meeting-display';

/**
 * Stundenplan des bearbeiteten Dozenten. Eine reine Meetingliste taugt hier nicht: seit ein
 * Meeting je Dozent und Schultag entsteht, unterscheiden sich die Einträge nur über das Datum.
 */
@Component({
  selector: 'app-teams-meeting-preview',
  standalone: true,
  imports: [
    AsyncPipe,
    // Zyklus-Kette DialogManagerService.
    forwardRef(() => DocentTimetableTableComponent),
    DocentScheduleHashComponent,
    forwardRef(() => MeetingLinkDocentActionsComponent),
  ],
  templateUrl: './teams-meeting-preview.html',
  styleUrl: './teams-meeting-preview.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamsMeetingPreview implements OnInit {
  @Input({ required: true }) selectedUser: user;

  /** Dozenten-Benutzer für den Meeting-Dialog, kommen aus den Daten des umgebenden Dialogs */
  public lecturerUsers: user[] = [];

  timetable$: Observable<DocentTimetableRow[]>;

  constructor(
    private notificationService: NotificationService,
    private dialogConfig: DynamicDialogConfig,
    private readonly docentTimetableService: DocentTimetableService,
  ) {}

  ngOnInit(): void {
    if (!this.dialogConfig.data?.lecturerUsers) {
      this.notificationService.error(
        'Keine Dozenten-Benutzer gefunden. Bitte versuchen Sie es erneut.',
      );
    }
    this.lecturerUsers = this.dialogConfig.data?.lecturerUsers ?? [];

    this.timetable$ = this.loadTimetable(this.selectedUser.fullName);
  }

  /** Lädt den Stundenplan neu, z. B. nach Erstellen/Bearbeiten eines Meetings. */
  reloadTimetable(): void {
    this.timetable$ = this.loadTimetable(this.selectedUser.fullName);
  }

  private loadTimetable(docentName: string) {
    const from = new Date();
    const to = new Date();
    to.setDate(from.getDate() + DEFAULT_RANGE_IN_DAYS);

    return this.docentTimetableService
      .getTimetable(toIsoDate(from), toIsoDate(to), docentName)
      .pipe(
        map((entries) => toDocentTimetableRows(entries)),
        catchError(() => {
          this.notificationService.error('Fehler beim Laden des Stundenplans');
          return of([] as DocentTimetableRow[]);
        }),
      );
  }
}
